import { useEffect, useState } from 'react';
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';
import { CheckinItem, enrichClientCheckin } from 'src/api/checkins';

export function useCheckinRealtime(checkinId?: string, chatId?: string, trainerUidProp?: string) {
  const [checkin, setCheckin] = useState<CheckinItem | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [activeUid, setActiveUid] = useState<string>(() => {
    return (
      trainerUidProp || auth.currentUser?.uid || sessionStore.getState().currentUser?.uid || ''
    );
  });

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user?.uid) {
        setActiveUid(user.uid);
      } else {
        const storedUid = sessionStore.getState().currentUser?.uid || trainerUidProp || '';
        if (storedUid) {
          setActiveUid(storedUid);
        }
      }
    });
    return () => unsubscribeAuth();
  }, [trainerUidProp]);

  useEffect(() => {
    if (!checkinId) {
      setCheckin(null);
      setLoading(false);
      return;
    }

    const trainerUid =
      activeUid ||
      auth.currentUser?.uid ||
      sessionStore.getState().currentUser?.uid ||
      trainerUidProp ||
      '';

    if (!trainerUid) {
      setLoading(true);
      return;
    }

    setLoading(true);

    let unsubMsg: (() => void) | null = null;
    const foundChatId = chatId;

    const attachMsgListener = (effChatId: string, msgId: string) => {
      if (unsubMsg) return;
      try {
        const msgDocRef = doc(db, 'flutter_chat_messages', effChatId, 'messages', msgId);
        unsubMsg = onSnapshot(
          msgDocRef,
          (msgSnap) => {
            if (msgSnap.exists()) {
              const mData = msgSnap.data() as Record<string, any>;
              mData.id = msgSnap.id;
              const dateStr = mData.checkinDate || '';
              const enriched = enrichClientCheckin(mData, dateStr, trainerUid);
              setCheckin((prev) => (prev ? { ...prev, ...enriched } : enriched));
              setLoading(false);
              setError(null);
            }
          },
          (err) => {
            console.warn('Realtime message subscription notice:', err);
          },
        );
      } catch (err) {
        console.warn('Error attaching message listener:', err);
      }
    };

    if (foundChatId) {
      attachMsgListener(foundChatId, checkinId);
    }

    const checkinListCol = collection(db, 'trainer_app_users', trainerUid, 'checkinList');
    const unsubscribeList = onSnapshot(
      checkinListCol,
      (snapshot) => {
        let foundItem: CheckinItem | null = null;
        for (const docSnapshot of snapshot.docs) {
          const docData = docSnapshot.data() as Record<string, any>;
          const dateStr = docData.date || docSnapshot.id;

          for (const listKey of ['morningList', 'daytimeList', 'eveningList']) {
            const slotList = (docData[listKey] || []) as Array<Record<string, any>>;
            for (let idx = 0; idx < slotList.length; idx++) {
              const slot = slotList[idx];
              const slotId =
                slot.id || slot.checkin_id || `${dateStr}_${slot.time || '00:00'}_${idx}`;
              if (
                slot.message_id === checkinId ||
                slot.id === checkinId ||
                slot.checkin_id === checkinId ||
                slotId === checkinId ||
                `${dateStr}_${slot.time}` === checkinId ||
                `${dateStr}_${slot.time}_${idx}` === checkinId
              ) {
                foundItem = enrichClientCheckin(slot, dateStr, trainerUid);
                break;
              }
            }
            if (foundItem) break;
          }
          if (foundItem) break;
        }

        if (foundItem) {
          const clientUid = foundItem.client_uid || foundItem.appointUId;
          const effChatId = foundItem.chat_id || (clientUid ? `${trainerUid}_${clientUid}` : '');
          if (effChatId && !unsubMsg) {
            attachMsgListener(effChatId, foundItem.message_id || checkinId);
          }

          if (clientUid && !foundItem.client_email) {
            getDoc(doc(db, 'flutter_user', clientUid))
              .then((uSnap) => {
                if (uSnap.exists()) {
                  const uData = uSnap.data() as Record<string, any>;
                  setCheckin((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      client_email: uData.email || prev.client_email || '',
                      client_name:
                        prev.client_name === 'Client' ||
                        prev.client_name === 'Guest' ||
                        !prev.client_name
                          ? uData.name ||
                            `${uData.name || ''} ${uData.surname || ''}`.trim() ||
                            prev.client_name
                          : prev.client_name,
                      client_avatar:
                        prev.client_avatar ||
                        uData.avatar ||
                        uData.avatar_url ||
                        uData.profile_picture ||
                        null,
                    };
                  });
                }
              })
              .catch((err) => {
                console.warn('Could not fetch flutter_user doc in useCheckinRealtime:', err);
              });
          }
          setCheckin((prev) => {
            if (!prev) return foundItem;
            // Merge slot with prev (which might have message updates)
            return { ...foundItem, ...prev, id: foundItem!.id };
          });
          setError(null);
        } else if (!foundChatId && !unsubMsg) {
          setCheckin((prev) => {
            if (prev) return prev;
            setError('ERR_CHECKIN_NOT_FOUND');
            return null;
          });
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error in checkin realtime checkinList subscription:', err);
        setError(err.message || 'Failed to sync check-in');
        setLoading(false);
      },
    );

    return () => {
      unsubscribeList();
      if (unsubMsg) unsubMsg();
    };
  }, [checkinId, chatId, activeUid, trainerUidProp]);

  return { checkin, loading, error };
}
